import React, { useState } from "react";
import SaveButton from "../../../components/Buttons/SaveButton";
import DateField from "../../../components/Fields/DateField";
import DecimalField from "../../../components/Fields/DecimalField";
import SelectListField from "../../../components/Fields/SelectListField";
import TextareaField from "../../../components/Fields/TextareaField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import { useNotifications } from "../../../contexts/notificationContext";
import DataService from "../../../services/data.service";
import { ItemItemRevisionPopupProps } from "../../../types/PopupProps";
import { DataResult } from "../../../types/Result";

interface PriceCreateModel {
    itemId: number
    priceTypeId: number
    priceTrancheTypeId?: number
    priceTrancheId?: number
    currencyId?: number
    amount?: number
    businessPartnerId?: number
    incoTermId?: number
    beginDate?: string
    endDate?: string
    remarks?: string

}

const AddPricePopup: React.FC<ItemItemRevisionPopupProps> = ({ show, onHide, itemId, itemRevisionId, itemTypeId }) => {
    const [serverErrors, setServerErrors] = useState<DataResult<PriceCreateModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();

    const [formData, setFormData] = useState<PriceCreateModel>({
        itemId: itemId,
        priceTypeId: 0,
    });

    const priceTypes = useSelectListOptions('api/v1/selectlist/PriceTypes', '-- Select price type');
    const businessPartners = useSelectListOptions('api/v1/selectlist/BusinessPartners?activeOnly=true', '-- Select business-partner');
    const priceTrancheTypes = useSelectListOptions('api/v1/selectlist/PriceTrancheTypes', '-- No volume based pricing');
    const priceTranches = useSelectListOptions('api/v1/selectlist/PriceTranches?priceTrancheTypeId=' + formData.priceTrancheTypeId, '-- No volume based pricing');
    const currencies = useSelectListOptions('api/v1/selectlist/Currencies', '-- Select currency');
    const incoTerms = useSelectListOptions('api/v1/selectlist/IncoTerms', '-- Select incoterms');

    function submit() {
        if (formData) {
            DataService
                .create<PriceCreateModel>(`api/v1/Prices`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true) {
                        onHide();
                    }
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'priceTypeId', label: 'Type', validationRules: [{ type: 'isRequired' }] },
            { name: 'businessPartnerId', label: 'Business-partner' },
            { name: 'priceTrancheTypeId', label: 'Tranche type' },
            { name: 'priceTrancheId', label: 'Volume' },
            { name: 'currencyId', label: 'Currency' },
            { name: 'amount', label: 'Price', validationRules: [{ type: 'isRequired' }] },
            { name: 'incoTermId', label: 'IncoTerms' },
            { name: 'beginDate', label: 'Begin date', validationRules: [{ type: 'isRequired' }] },
            { name: 'endDate', label: 'End date' },
            { name: 'remarks', label: 'Remarks' },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    return (
        <Popup show={show} onHide={onHide} size={'lg'} title={'Add price'} >
            <Popup.Body>
                <SelectListField {...getFieldProps('priceTypeId')} options={priceTypes} />
                <SelectListField {...getFieldProps('businessPartnerId')} options={businessPartners} />
                <SelectListField {...getFieldProps('priceTrancheTypeId')} options={priceTrancheTypes} />
                <SelectListField {...getFieldProps('priceTrancheId')} options={priceTranches} />
                <SelectListField {...getFieldProps('currencyId')} options={currencies} />
                <DecimalField {...getFieldProps('amount')} />
                <SelectListField {...getFieldProps('incoTermId')} options={incoTerms} />
                <DateField {...getFieldProps('beginDate')} />
                <DateField {...getFieldProps('endDate')} />
                <TextareaField {...getFieldProps('remarks')} />
            </Popup.Body>
            <Popup.Footer>
                <SaveButton {...getSubmitButtonProps()} text='Add' />
            </Popup.Footer>
        </Popup>
    );
}
export default AddPricePopup;